<template>
  <div class="d-flex flex-column" style="gap: 1rem;">
    <div class="d-flex justify-content-end">
      <b-button variant="primary" @click="handleSave" :disabled="loading">
        {{ translations.general.btn_save }}
      </b-button>
    </div>
    <b-row>
      <b-col>
        <b-card
        :header="translations.general.general_card_title"
        border-variant="light">
        <b-row>
          <b-col cols="6">
            <b-form-group
              label-class="p-0"
              :label="translations.general.form.customer_success_manager">
              <b-form-input readonly v-model="client.rvp_name"/>
            </b-form-group>
            <b-form-group
              label-class="p-0"
              :label="translations.general.form.revops_contact"
              :invalid-feedback="handleError('form.revops_contact')"
              :state="!isValidating || isValid('form.revops_contact')">
              <b-select
                v-model="form.revops_contact"
                :options="CLAIM_ASSIGNEES"
                :disabled="loading"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group
              label-class="p-0"
              :label="translations.general.form.billing_type"
              :invalid-feedback="handleError('form.billing_type')"
              :state="!isValidating || isValid('form.billing_type')">
              <b-select
                v-model="form.billing_type"
                :options="billingTypeOptions"
                :disabled="loading"/>
            </b-form-group>
            <b-form-group label-class="p-0" :label="translations.general.form.data_access">
              <b-form-input readonly v-model="client.data_access"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card
        :header="translations.general.direct_card_title"
        border-variant="light">
        <b-row>
          <b-col cols="6">
            <b-form-group label-class="p-0" :label="translations.general.form.billing_contact">
              <b-form-input v-model="form.billing_contact" :disabled="loading"/>
            </b-form-group>
            <b-form-group label-class="p-0" :label="translations.general.form.billing_address">
              <b-form-input v-model="form.billing_address" :disabled="loading"/>
            </b-form-group>
            <b-form-group label-class="p-0" :label="translations.general.form.po_number">
              <b-form-input v-model="form.po_number" :disabled="loading"/>
            </b-form-group>
            <b-form-group
              label-class="p-0"
              :label="translations.general.form.payment_method"
              :invalid-feedback="handleError('form.payment_method')"
              :state="!isValidating || isValid('form.payment_method')">
              <b-select
                v-model="form.payment_method"
                :options="paymentMethodOptions"
                :disabled="loading"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group
              label-class="p-0"
              :label="translations.general.form.payment_terms"
              :invalid-feedback="handleError('form.payment_terms')"
              :state="!isValidating || isValid('form.payment_terms')">
              <b-select
                v-model="form.payment_terms"
                :options="paymentTermsOptions"
                :disabled="loading"/>
            </b-form-group>
            <b-form-group label-class="p-0" :label="translations.general.form.other_billing_requirements">
              <b-form-textarea rows="4" v-model="form.other_billing_requirements" :disabled="loading"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import { CLAIM_ASSIGNEES } from '@/constants/finance';
import { parseResponseError } from '@/http/parsers/parse_response';
import errorHandler from '@/mixins/errorHandler';
import translations from '@/translations';
import financeService from '@/services/finance-service';

export default {
  name: 'GeneralTab',
  mixins: [ errorHandler ],
  props: {
    client: {
      type: Object,
      required: true,
    },
    information: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      CLAIM_ASSIGNEES,
      translations: translations.finance.client_configs.implementation_information,
      isValidating: false,
      form: {},
    };
  },
  beforeMount() {
    if (this.information?.id) {
      this.form = cloneDeep(this.information);
    }
  },
  validations() {
    return this.rules;
  },
  computed: {
    rules() {
      return {
        form: {
          revops_contact: { required },
          billing_type: { required },
          payment_terms: { required },
          payment_method: { required },
        },
      };
    },
    billingTypeOptions() {
      return [
        { value: 'claims', text: 'Claims' },
        { value: 'direct', text: 'Direct' },
        { value: 'both', text: 'Claims/Direct' },
      ];
    },
    paymentTermsOptions() {
      return [
        { value: 'net30', text: 'Net 30' },
        { value: 'net45', text: 'Net 45' },
        { value: 'net60', text: 'Net 60' },
        { value: 'net90', text: 'Net 90' },
      ];
    },
    paymentMethodOptions() {
      return [
        { value: 'eft', text: 'EFT' },
        { value: 'check', text: 'Check' },
        { value: 'both', text: 'EFT/Check' },
      ];
    },
  },
  methods: {
    async handleSave() {
      this.isValidating = true;
      if (this.$v.form.$invalid) {
        return;
      }

      try {
        this.loading = true;
        if (this.information?.id) {
          this.information = await financeService.updateImplementationInfo(this.client.id, this.form);
        } else {
          this.information = await financeService.createImplementationInfo(this.client.id, this.form);
        }
        this.$noty.success(this.translations.success.save);
      } catch (error) {
        this.$noty.error(`${this.translations.errors.save}: ${parseResponseError(error)}`);
      } finally {
        this.isValidating = false;
        this.loading = false;
      }
    },
  },
};
</script>
